export const TABS = ["all", "awaiting", "done"];
export const DEFAULT_TAB = "awaiting";

export const KINDS = {
  in: {
    title: "Checking In Today",
    labels: { all: "All", awaiting: "Awaiting check-in", done: "Checked in" },
    nobody: "No dogs arriving today",
    allDone: "Everyone has checked in",
    noneDone: "No one has checked in yet",
  },
  out: {
    title: "Checking Out Today",
    labels: { all: "All", awaiting: "Awaiting check-out", done: "Checked out" },
    nobody: "No dogs departing today",
    allDone: "Everyone has checked out",
    noneDone: "No one has checked out yet",
  },
  facility: {
    title: "In Facility Today",
    labels: { all: "All", done: "Checked in", awaiting: "Not checked in" },
    order: ["all", "done", "awaiting"],
    defaultTab: "all",
    sorted: false,
    nobody: "No dogs checked in right now",
    allDone: "Everyone has checked in",
    noneDone: "No dogs checked in right now",
  },
};

export function hasTabs(kind) {
  return KINDS[kind].tabs !== false;
}

export function tabsFor(kind) {
  return (kind && KINDS[kind].order) || TABS;
}

export function defaultTab(kind) {
  return KINDS[kind].defaultTab || DEFAULT_TAB;
}

export function isSorted(kind) {
  return KINDS[kind].sorted !== false;
}

export function stateFor({ checkedIn, checkedOut }) {
  if (checkedOut) return "out";
  if (checkedIn) return "in";
  return "none";
}

export function isDone(kind, state) {
  if (kind === "facility") return state === "in";
  return kind === "in" ? state !== "none" : state === "out";
}

export function matchesTab(kind, tab, state) {
  if (tab === "all") return true;
  return tab === "done" ? isDone(kind, state) : !isDone(kind, state);
}

export function tally(kind, states) {
  const done = states.filter((state) => isDone(kind, state)).length;
  return { all: states.length, awaiting: states.length - done, done };
}

export function tabLabel(kind, tab, counts) {
  const label = KINDS[kind].labels[tab];
  return counts ? `${label} (${counts[tab]})` : label;
}

function plural(count, word) {
  return `${count} ${word}${count === 1 ? "" : "s"}`;
}

export function summaryLabel(dogs, bookings, { sorted = true, notCheckedIn = 0 } = {}) {
  const parts = [plural(dogs, "dog"), plural(bookings, "booking")];
  if (notCheckedIn > 0) parts.push(`${notCheckedIn} not checked in`);
  if (sorted) parts.push("sorted by time");
  return parts.join(" · ");
}

export function toggleLabel(petName, state) {
  if (state === "none") return `Check in ${petName}`;
  if (state === "in") return `Check out ${petName}`;
  return `Reset ${petName}'s attendance`;
}

export function normalizeQuery(value) {
  return String(value == null ? "" : value).trim();
}

export function matchesQuery(fields, query) {
  const needle = normalizeQuery(query).toLowerCase();
  if (!needle) return true;

  return fields.some((field) => String(field == null ? "" : field).toLowerCase().includes(needle));
}

export function resultLabel(count) {
  return `${count} result${count === 1 ? "" : "s"}`;
}

export function noMatchesMessage(query) {
  return `No matches for \u201c${normalizeQuery(query)}\u201d`;
}

export function emptyMessage(kind, tab, counts, query = "") {
  if (normalizeQuery(query)) return noMatchesMessage(query);

  const copy = KINDS[kind];
  if (!counts || counts.all === 0) return copy.nobody;
  return tab === "awaiting" ? copy.allDone : copy.noneDone;
}

export function nextTab(current, key, tabs = TABS) {
  const index = tabs.indexOf(current);
  if (key === "ArrowRight") return tabs[(index + 1) % tabs.length];
  if (key === "ArrowLeft") return tabs[(index + tabs.length - 1) % tabs.length];
  if (key === "Home") return tabs[0];
  if (key === "End") return tabs[tabs.length - 1];
  return null;
}

export function fitPanel({ anchor, viewport, panelHeight, gap = 8, margin = 12, minHeight = 240 }) {
  const roomAbove = anchor.top - viewport.top - gap - margin;
  const roomBelow = viewport.bottom - anchor.bottom - gap - margin;
  const side = roomAbove >= panelHeight || roomAbove >= roomBelow ? "up" : "down";
  const room = side === "up" ? roomAbove : roomBelow;

  return { side, maxHeight: Math.max(minHeight, Math.floor(room)) };
}

export function placePanel({ anchor, container, viewport, panelWidth, panelHeight, gap = 8 }) {
  const maxLeft = Math.max(0, container.width - panelWidth);
  const left = Math.min(Math.max(0, anchor.left - container.left), maxLeft);
  const { side, maxHeight } = fitPanel({ anchor, viewport, panelHeight, gap });

  if (side === "up") {
    return { side, left, bottom: container.bottom - anchor.top + gap, maxHeight };
  }

  return { side, left, top: anchor.bottom - container.top + gap, maxHeight };
}

export function viewportBounds(doc = document, win = window) {
  const bar = doc.querySelector(".sticky-top-bar");
  const top = bar ? Math.max(0, bar.getBoundingClientRect().bottom) : 0;
  return { top, bottom: win.innerHeight };
}
